/**
 * Команды: кто в них работает и какие аккаунты за ними закреплены.
 *
 * ── Что здесь изменилось ────────────────────────────────────────────────────
 *
 * Было две подвкладки. «Telegram-аккаунты» показывали плоский список всех
 * аккаунтов, «Папки и команды» — дерево команд отдельно от него. Разложить
 * аккаунт по командам можно было только во второй, а посмотреть, что это за
 * аккаунт, — только в первой.
 *
 * Теперь слева дерево, справа содержимое выбранной команды. Ровно два уровня,
 * как их и просили видеть:
 *
 *   1. сотрудники — кто в этой команде работает;
 *   2. аккаунты — с какими Telegram-аккаунтами она работает.
 *
 * ── Почему уровней ровно два ────────────────────────────────────────────────
 *
 * Ветка и команда внутри неё. Третьего нет намеренно: команда внутри команды —
 * это уже оргструктура, а не разграничение доступа, и дерево, в котором никто
 * не может ответить, кто что видит, хуже плоского списка.
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import type { AnyTelegramAccountDto, UserDto, UserSummaryDto } from "@tw/shared-types";
import type { AccountFolderTreeDto } from "../lib/api.js";
import { useSession } from "../state/session.tsx";
import { describeError } from "../lib/errors.js";
import { accountLabel } from "./accountLabel.js";
import { connectionLabel } from "../lib/labels.js";
import { ROLE_LABELS, roleTone } from "../lib/roles.js";
import { ADMIN_PANEL, ADMIN_PANEL_TITLE } from "./adminPanel.js";
import { AccessDialog } from "./AccountAccessDialog.tsx";
import { ConnectAccountPanel } from "./ConnectAccountPanel.tsx";
import { StateBadge } from "./StateBadge.tsx";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { FIELD } from "./controls.js";
import { ChevronIcon, PencilIcon, PlusIcon, SearchIcon, TrashIcon } from "./icons.tsx";
import { cn } from "@/lib/utils";

type AnyUser = UserDto | UserSummaryDto;

const TRACKS: ReadonlyArray<{
  value: "MANAGER" | "OPERATOR" | "HR";
  label: string;
}> = [
  { value: "MANAGER", label: "Менеджеры" },
  { value: "OPERATOR", label: "Операторы" },
  { value: "HR", label: "HR" },
];

/** Нераспределённые — не папка, а строка списка. */
const LOOSE = "__loose__";
/**
 * Всё сразу.
 *
 * Нужен обязательно: без него единственный способ увидеть все аккаунты — обойти
 * все команды по очереди, а именно за общим списком чаще всего и приходят.
 */
const ALL = "__all__";

export function TeamsPanel({
  accounts,
  users,
  canAssign,
  canAssignSenior,
  onChanged,
}: {
  accounts: AnyTelegramAccountDto[];
  users: AnyUser[];
  canAssign: boolean;
  canAssignSenior: boolean;
  onChanged: () => void;
}) {
  const { api } = useSession();

  const [tree, setTree] = useState<AccountFolderTreeDto | null>(null);
  const [selected, setSelected] = useState<string>(ALL);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [creating, setCreating] = useState<{ parentId: string | null } | null>(
    null,
  );
  const [renaming, setRenaming] = useState<string | null>(null);
  const [connecting, setConnecting] = useState(false);
  const [openedAccount, setOpenedAccount] =
    useState<AnyTelegramAccountDto | null>(null);
  const [adding, setAdding] = useState<"users" | "accounts" | null>(null);
  /** Поиск по составу выбранной команды. При выборе «Все» без него не обойтись. */
  const [query, setQuery] = useState("");

  const load = useCallback(async () => {
    try {
      setTree(await api.accountFolders());
      setError(null);
    } catch (cause) {
      setError(describeError(cause));
    }
  }, [api]);

  useEffect(() => {
    void load();
  }, [load]);

  /*
   * Любое изменение перечитывает дерево целиком.
   *
   * Точечное обновление состояния стоило бы дороже, чем один запрос:
   * перемещение аккаунта меняет состав ДВУХ папок, удаление — ещё и состав
   * вложенных, а расхождение экрана с базой в вопросе доступа заметят не сразу
   * и не по тому месту.
   */
  const act = async (fn: () => Promise<unknown>): Promise<void> => {
    setBusy(true);
    setError(null);
    try {
      await fn();
      await load();
      onChanged();
    } catch (cause) {
      setError(describeError(cause));
    } finally {
      setBusy(false);
    }
  };

  const accountById = useMemo(
    () => new Map(accounts.map((a) => [a.id, a])),
    [accounts],
  );

  if (!tree) {
    return (
      <section className={ADMIN_PANEL}>
        <p className="text-sm text-muted-foreground">
          {error ?? "Загружаем команды…"}
        </p>
      </section>
    );
  }

  const roots = tree.folders.filter((f) => f.parentId === null);
  const childrenOf = (id: string) =>
    tree.folders.filter((f) => f.parentId === id);

  const current =
    selected === LOOSE || selected === ALL
      ? null
      : (tree.folders.find((f) => f.id === selected) ?? null);

  /*
   * Состав команды.
   *
   * У ветки показываем и её собственных, и всех из вложенных команд: ветка —
   * это и есть объединение своих команд, и пустой список у неё при полных
   * вложенных читался бы как ошибка.
   */
  const scopeIds =
    selected === LOOSE || selected === ALL
      ? []
      : [selected, ...childrenOf(selected).map((f) => f.id)];

  const teamUsers =
    selected === ALL
      ? tree.users
      : selected === LOOSE
        ? tree.users.filter((u) => !u.folderId)
        : tree.users.filter((u) => u.folderId && scopeIds.includes(u.folderId));

  const teamAccounts =
    selected === ALL
      ? tree.accounts
      : selected === LOOSE
        ? tree.accounts.filter((a) => !a.folderId)
        : tree.accounts.filter(
            (a) => a.folderId && scopeIds.includes(a.folderId),
          );

  /** Раскладывать по командам можно только внутри команды. */
  const inTeam = selected !== LOOSE && selected !== ALL;

  const needle = query.trim().toLocaleLowerCase();
  const shownUsers = teamUsers.filter(
    (u) => !needle || u.displayName.toLocaleLowerCase().includes(needle),
  );
  const shownAccounts = teamAccounts.filter(
    (a) => !needle || a.alias.toLocaleLowerCase().includes(needle),
  );

  const title =
    selected === ALL
      ? "Все"
      : selected === LOOSE
        ? "Без команды"
        : (current?.name ?? "Команда");

  return (
    <section className={cn(ADMIN_PANEL, "flex flex-col gap-4")}>
      <div className="flex flex-wrap items-center gap-2">
        <div className="mr-auto">
          <h2 className={ADMIN_PANEL_TITLE}>Команды и аккаунты</h2>
          <p className="mt-1 text-sm leading-relaxed text-muted-foreground">
            Слева команды, справа их состав: сначала сотрудники, затем
            закреплённые за командой Telegram-аккаунты.
          </p>
        </div>

        <Button
          variant="brand"
          size="sm"
          onClick={() => setConnecting(true)}
          className="flex-none gap-1.5"
        >
          <PlusIcon className="size-4" />
          Подключить аккаунт
        </Button>
      </div>

      {error && (
        <p role="alert" className="rounded-xl bg-destructive/10 px-3 py-2 text-sm text-destructive">
          {error}
        </p>
      )}

      <div className="grid gap-4 md:grid-cols-[minmax(200px,270px)_1fr]">
        {/* ── Дерево команд ─────────────────────────────────────────────── */}
        <div className="flex flex-col gap-1">
          <div className="flex items-center justify-between">
            <p className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
              Команды
            </p>
            <Button
              variant="ghost"
              size="icon-sm"
              onClick={() => setCreating({ parentId: null })}
              aria-label="Новая ветка"
              title="Новая ветка"
              className="text-muted-foreground"
            >
              <PlusIcon className="size-4" />
            </Button>
          </div>

          <FolderRow
            name="Все"
            users={tree.users.length}
            accounts={tree.accounts.length}
            active={selected === ALL}
            onClick={() => setSelected(ALL)}
          />

          {roots.map((root) => (
            <div key={root.id} className="flex flex-col">
              <FolderRow
                name={root.name}
                hint={TRACKS.find((t) => t.value === root.track)?.label}
                users={
                  tree.users.filter(
                    (u) =>
                      u.folderId === root.id ||
                      childrenOf(root.id).some((c) => c.id === u.folderId),
                  ).length
                }
                accounts={
                  tree.accounts.filter(
                    (a) =>
                      a.folderId === root.id ||
                      childrenOf(root.id).some((c) => c.id === a.folderId),
                  ).length
                }
                active={selected === root.id}
                onClick={() => setSelected(root.id)}
                onAddChild={() => setCreating({ parentId: root.id })}
                onRename={() => setRenaming(root.id)}
              />

              {childrenOf(root.id).map((team) => (
                <FolderRow
                  key={team.id}
                  nested
                  name={team.name}
                  users={tree.users.filter((u) => u.folderId === team.id).length}
                  accounts={
                    tree.accounts.filter((a) => a.folderId === team.id).length
                  }
                  active={selected === team.id}
                  onClick={() => setSelected(team.id)}
                  onRename={() => setRenaming(team.id)}
                />
              ))}
            </div>
          ))}

          <FolderRow
            name="Без команды"
            users={tree.users.filter((u) => !u.folderId).length}
            accounts={tree.accounts.filter((a) => !a.folderId).length}
            active={selected === LOOSE}
            onClick={() => setSelected(LOOSE)}
          />

          {roots.length === 0 && (
            <p className="px-2 py-1 text-[12px] leading-relaxed text-muted-foreground">
              Веток пока нет. Ветка — это «Менеджеры», «Операторы», «HR»;
              команды заводятся внутри неё.
            </p>
          )}
        </div>

        {/* ── Состав выбранной команды ──────────────────────────────────── */}
        <div className="flex flex-col gap-5">
          <div className="flex items-center gap-2">
            <h3 className="min-w-0 flex-none truncate text-sm font-semibold text-heading">
              {title}
            </h3>

            <div className="relative ml-auto min-w-0 flex-1 md:max-w-xs">
              <SearchIcon className="pointer-events-none absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Поиск по составу"
                aria-label="Поиск по составу команды"
                className={cn(FIELD, "w-full pl-8")}
              />
            </div>
            {current && (
              <Button
                variant="ghost"
                size="sm"
                disabled={busy}
                onClick={() => {
                  // Удаление уносит саму папку; содержимое не пропадает, а
                  // становится нераспределённым. Подтверждение называет
                  // последствие, а не спрашивает «вы уверены».
                  if (
                    window.confirm(
                      `Удалить «${current.name}»?\n\n` +
                        "Сотрудники и аккаунты не пропадут — они станут без команды.",
                    )
                  ) {
                    void act(async () => {
                      await api.deleteAccountFolder(current.id);
                      setSelected(LOOSE);
                    });
                  }
                }}
                className="flex-none gap-1.5 text-muted-foreground hover:text-destructive"
              >
                <TrashIcon className="size-4" />
                Удалить команду
              </Button>
            )}
          </div>

          {/* Уровень 1: сотрудники */}
          <MemberSection
            label="Сотрудники"
            count={shownUsers.length}
            onAdd={inTeam ? () => setAdding("users") : undefined}
            addLabel="Добавить сотрудника"
            empty="В этой команде пока никого нет."
          >
            {shownUsers.map((u) => (
              <li
                key={u.id}
                className="flex items-center gap-2 rounded-xl border border-transparent bg-accent/60 px-3 py-2"
              >
                <span
                  aria-hidden
                  className="size-2 flex-none rounded-full"
                  style={{ background: roleTone(u.role) }}
                />
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm font-medium text-heading">
                    {u.displayName}
                  </span>
                  <span className="block truncate text-[11px] text-muted-foreground">
                    {ROLE_LABELS[u.role] ?? u.role}
                  </span>
                </span>
                {inTeam && (
                  <Button
                    variant="ghost"
                    size="icon-sm"
                    disabled={busy}
                    onClick={() => void act(() => api.moveUserToFolder(u.id, null))}
                    aria-label={`Убрать ${u.displayName} из команды`}
                    title="Убрать из команды"
                    className="flex-none text-muted-foreground hover:text-destructive"
                  >
                    <TrashIcon className="size-4" />
                  </Button>
                )}
              </li>
            ))}
          </MemberSection>

          {/* Уровень 2: аккаунты */}
          <MemberSection
            label="Telegram-аккаунты"
            count={shownAccounts.length}
            onAdd={inTeam ? () => setAdding("accounts") : undefined}
            addLabel="Закрепить аккаунт"
            empty="За этой командой аккаунтов пока нет."
          >
            {shownAccounts.map((a) => {
              const full = accountById.get(a.id);
              return (
                <li
                  key={a.id}
                  className="flex items-center gap-2 rounded-xl border border-transparent bg-accent/60 px-3 py-2"
                >
                  <span
                    aria-hidden
                    className={cn(
                      "status-dot flex-none",
                      full?.connectionState === "CONNECTED"
                        ? "text-dot-done"
                        : "text-dot-progress",
                    )}
                  />
                  <button
                    type="button"
                    disabled={!full}
                    onClick={() => full && setOpenedAccount(full)}
                    className="min-w-0 flex-1 border-transparent bg-transparent p-0 text-left"
                  >
                    <span className="block truncate text-sm font-medium text-heading">
                      {full ? accountLabel(full) : a.alias}
                    </span>
                    <span className="block truncate text-[11px] text-muted-foreground">
                      {full
                        ? connectionLabel(full.connectionState)
                        : "нет доступа к карточке"}
                    </span>
                  </button>

                  {full && <StateBadge state={full.connectionState} />}

                  {a.folderId && (
                    <Button
                      variant="ghost"
                      size="icon-sm"
                      disabled={busy}
                      onClick={() =>
                        void act(() => api.moveAccountToFolder(a.id, null))
                      }
                      aria-label={`Открепить ${a.alias}`}
                      title="Открепить от команды"
                      className="flex-none text-muted-foreground hover:text-destructive"
                    >
                      <TrashIcon className="size-4" />
                    </Button>
                  )}
                </li>
              );
            })}
          </MemberSection>
        </div>
      </div>

      <CreateFolderDialog
        target={creating}
        busy={busy}
        roots={roots}
        onClose={() => setCreating(null)}
        onSubmit={(name, track, parentId) =>
          void act(async () => {
            await api.createAccountFolder({ name, track, parentId });
            setCreating(null);
          })
        }
      />

      <RenameFolderDialog
        folder={
          renaming ? (tree.folders.find((f) => f.id === renaming) ?? null) : null
        }
        busy={busy}
        onClose={() => setRenaming(null)}
        onSubmit={(name) =>
          void act(async () => {
            await api.renameAccountFolder(renaming ?? "", name);
            setRenaming(null);
          })
        }
      />

      <AddToTeamDialog
        kind={adding}
        teamName={title}
        busy={busy}
        users={tree.users.filter((u) => !scopeIds.includes(u.folderId ?? ""))}
        accounts={tree.accounts.filter(
          (a) => !scopeIds.includes(a.folderId ?? ""),
        )}
        onClose={() => setAdding(null)}
        onPickUser={(id) =>
          void act(() => api.moveUserToFolder(id, selected))
        }
        onPickAccount={(id) =>
          void act(() => api.moveAccountToFolder(id, selected))
        }
      />

      <Dialog open={connecting} onOpenChange={setConnecting}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle>Подключение аккаунта</DialogTitle>
            <DialogDescription>
              Аккаунт подключается по номеру телефона или QR-коду. Закрепить его
              за командой можно сразу после подключения.
            </DialogDescription>
          </DialogHeader>
          <DialogBody>
            <ConnectAccountPanel
              accounts={accounts}
              onChanged={() => {
                onChanged();
                void load();
              }}
              embedded
            />
          </DialogBody>
        </DialogContent>
      </Dialog>

      <AccessDialog
        account={openedAccount}
        users={users}
        canAssign={canAssign}
        canAssignSenior={canAssignSenior}
        onClose={() => setOpenedAccount(null)}
        onChanged={onChanged}
      />
    </section>
  );
}

/** Строка дерева: название, счётчики, действия при наведении. */
function FolderRow({
  name,
  hint,
  users,
  accounts,
  active,
  nested = false,
  onClick,
  onAddChild,
  onRename,
}: {
  name: string;
  hint?: string;
  users: number;
  accounts: number;
  active: boolean;
  nested?: boolean;
  onClick: () => void;
  onAddChild?: () => void;
  onRename?: () => void;
}) {
  return (
    <div
      className={cn(
        "group flex items-center gap-0.5 rounded-xl px-1 transition-colors",
        nested && "ml-3",
        active ? "bg-secondary" : "hover:bg-secondary/60",
      )}
    >
      <button
        type="button"
        onClick={onClick}
        aria-pressed={active}
        className="flex min-w-0 flex-1 items-center gap-2 border-transparent bg-transparent px-2 py-2 text-left"
      >
        {nested && (
          <ChevronIcon
            aria-hidden
            className="size-3 flex-none -rotate-90 text-muted-foreground"
          />
        )}
        <span className="min-w-0 flex-1 truncate">
          <span
            className={cn(
              "block truncate text-sm",
              active ? "font-medium text-brand" : "text-foreground",
            )}
          >
            {name}
          </span>
          {hint && (
            <span className="block truncate text-[11px] text-muted-foreground">
              {hint}
            </span>
          )}
        </span>
        {/*
          Два числа: людей и аккаунтов. Именно они и отвечают на вопрос, ради
          которого сюда заходят, — кто в команде и с чем она работает.
        */}
        <span className="flex-none text-[11px] text-muted-foreground">
          {users} / {accounts}
        </span>
      </button>

      {onRename && (
        <button
          type="button"
          onClick={onRename}
          aria-label={`Переименовать «${name}»`}
          title="Переименовать"
          className="grid size-6 flex-none place-items-center rounded-md border-transparent bg-transparent text-muted-foreground opacity-0 transition-opacity hover:text-brand focus-visible:opacity-100 group-hover:opacity-100"
        >
          <PencilIcon className="size-3.5" />
        </button>
      )}

      {onAddChild && (
        <button
          type="button"
          onClick={onAddChild}
          aria-label={`Новая команда в «${name}»`}
          title="Новая команда внутри ветки"
          className="grid size-6 flex-none place-items-center rounded-md border-transparent bg-transparent text-muted-foreground opacity-0 transition-opacity hover:text-brand focus-visible:opacity-100 group-hover:opacity-100"
        >
          <PlusIcon className="size-3.5" />
        </button>
      )}
    </div>
  );
}

function MemberSection({
  label,
  count,
  addLabel,
  empty,
  onAdd,
  children,
}: {
  label: string;
  count: number;
  addLabel: string;
  empty: string;
  onAdd?: () => void;
  children: React.ReactNode;
}) {
  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center justify-between gap-2">
        <p className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
          {label} · {count}
        </p>
        {onAdd && (
          <Button variant="secondary" size="sm" onClick={onAdd} className="gap-1.5">
            <PlusIcon className="size-3.5" />
            {addLabel}
          </Button>
        )}
      </div>

      {count === 0 ? (
        <p className="rounded-xl border border-border bg-card px-4 py-5 text-center text-sm text-muted-foreground">
          {empty}
        </p>
      ) : (
        <ul className="flex list-none flex-col gap-1.5 p-0">{children}</ul>
      )}
    </div>
  );
}

/**
 * Добавление в команду.
 *
 * Один список с поиском вместо перетаскивания: перетаскивание красиво
 * выглядит и плохо работает — на длинном списке до нужной строки надо ещё
 * доскроллить, не отпуская кнопку мыши.
 */
function AddToTeamDialog({
  kind,
  teamName,
  busy,
  users,
  accounts,
  onClose,
  onPickUser,
  onPickAccount,
}: {
  kind: "users" | "accounts" | null;
  teamName: string;
  busy: boolean;
  users: ReadonlyArray<{ id: string; displayName: string; role: string }>;
  accounts: ReadonlyArray<{ id: string; alias: string }>;
  onClose: () => void;
  onPickUser: (id: string) => void;
  onPickAccount: (id: string) => void;
}) {
  const [query, setQuery] = useState("");

  useEffect(() => {
    if (kind) setQuery("");
  }, [kind]);

  const needle = query.trim().toLocaleLowerCase();

  const visibleUsers = users.filter(
    (u) => !needle || u.displayName.toLocaleLowerCase().includes(needle),
  );
  const visibleAccounts = accounts.filter(
    (a) => !needle || a.alias.toLocaleLowerCase().includes(needle),
  );

  const empty =
    kind === "users" ? visibleUsers.length === 0 : visibleAccounts.length === 0;

  return (
    <Dialog open={kind !== null} onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {kind === "users" ? "Добавить в команду" : "Закрепить аккаунт"}
          </DialogTitle>
          <DialogDescription>
            {kind === "users"
              ? `Сотрудник перейдёт в «${teamName}». Из прежней команды он уйдёт: команда у человека одна.`
              : `Аккаунт закрепится за «${teamName}». За прежней командой он числиться перестанет.`}
          </DialogDescription>
        </DialogHeader>

        <DialogBody className="flex flex-col gap-2">
          <div className="relative">
            <SearchIcon className="pointer-events-none absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={kind === "users" ? "Имя сотрудника" : "Название аккаунта"}
              aria-label="Поиск"
              className={cn(FIELD, "w-full pl-8")}
            />
          </div>

          {empty ? (
            <p className="py-4 text-center text-sm text-muted-foreground">
              {needle ? "Ничего не нашлось." : "Добавлять некого — все уже здесь."}
            </p>
          ) : (
            <ul className="flex max-h-72 list-none flex-col gap-1.5 overflow-y-auto p-0">
              {kind === "users"
                ? visibleUsers.map((u) => (
                    <li key={u.id}>
                      <button
                        type="button"
                        disabled={busy}
                        onClick={() => onPickUser(u.id)}
                        className="flex w-full items-center gap-2 rounded-xl border border-transparent bg-accent/60 px-3 py-2 text-left transition-colors hover:bg-secondary disabled:opacity-45"
                      >
                        <span
                          aria-hidden
                          className="size-2 flex-none rounded-full"
                          style={{ background: roleTone(u.role) }}
                        />
                        <span className="min-w-0 flex-1">
                          <span className="block truncate text-sm text-heading">
                            {u.displayName}
                          </span>
                          <span className="block truncate text-[11px] text-muted-foreground">
                            {ROLE_LABELS[u.role] ?? u.role}
                          </span>
                        </span>
                      </button>
                    </li>
                  ))
                : visibleAccounts.map((a) => (
                    <li key={a.id}>
                      <button
                        type="button"
                        disabled={busy}
                        onClick={() => onPickAccount(a.id)}
                        className="flex w-full items-center gap-2 rounded-xl border border-transparent bg-accent/60 px-3 py-2 text-left text-sm text-heading transition-colors hover:bg-secondary disabled:opacity-45"
                      >
                        <span className="min-w-0 flex-1 truncate">{a.alias}</span>
                      </button>
                    </li>
                  ))}
            </ul>
          )}
        </DialogBody>

        <DialogFooter>
          <Button variant="ghost" size="sm" onClick={onClose}>
            Закрыть
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CreateFolderDialog({
  target,
  busy,
  roots,
  onClose,
  onSubmit,
}: {
  target: { parentId: string | null } | null;
  busy: boolean;
  roots: ReadonlyArray<{ id: string; name: string; track: string }>;
  onClose: () => void;
  onSubmit: (name: string, track: string, parentId: string | null) => void;
}) {
  const [name, setName] = useState("");
  const [track, setTrack] = useState<string>("MANAGER");

  useEffect(() => {
    if (target) {
      setName("");
      // Команда наследует ветку родителя: своя ветка у неё была бы враньём —
      // доступ всё равно определяет ветка сверху.
      const parent = roots.find((r) => r.id === target.parentId);
      setTrack(parent?.track ?? "MANAGER");
    }
  }, [target, roots]);

  const parentName = roots.find((r) => r.id === target?.parentId)?.name;

  return (
    <Dialog open={target !== null} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>
            {target?.parentId ? "Новая команда" : "Новая ветка"}
          </DialogTitle>
          <DialogDescription>
            {target?.parentId
              ? `Команда внутри ветки «${parentName}».`
              : "Ветка — верхний уровень: «Менеджеры», «Операторы», «HR». Команды заводятся внутри неё."}
          </DialogDescription>
        </DialogHeader>

        <DialogBody className="flex flex-col gap-3">
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Название"
            aria-label="Название"
            className={cn(FIELD, "w-full")}
          />

          {!target?.parentId && (
            <select
              value={track}
              onChange={(e) => setTrack(e.target.value)}
              aria-label="Ветка"
              className={cn(FIELD, "w-full")}
            >
              {TRACKS.map((t) => (
                <option key={t.value} value={t.value}>
                  {t.label}
                </option>
              ))}
            </select>
          )}
        </DialogBody>

        <DialogFooter>
          <Button variant="ghost" size="sm" onClick={onClose}>
            Отмена
          </Button>
          <Button
            size="sm"
            disabled={busy || !name.trim()}
            onClick={() => onSubmit(name.trim(), track, target?.parentId ?? null)}
          >
            Создать
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function RenameFolderDialog({
  folder,
  busy,
  onClose,
  onSubmit,
}: {
  folder: { id: string; name: string } | null;
  busy: boolean;
  onClose: () => void;
  onSubmit: (name: string) => void;
}) {
  const [name, setName] = useState("");

  useEffect(() => {
    setName(folder?.name ?? "");
  }, [folder]);

  return (
    <Dialog open={folder !== null} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>Переименование</DialogTitle>
          <DialogDescription>
            Состав команды и выданные доступы не меняются.
          </DialogDescription>
        </DialogHeader>

        <DialogBody>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            aria-label="Название"
            className={cn(FIELD, "w-full")}
          />
        </DialogBody>

        <DialogFooter>
          <Button variant="ghost" size="sm" onClick={onClose}>
            Отмена
          </Button>
          <Button
            size="sm"
            disabled={busy || !name.trim() || name.trim() === folder?.name}
            onClick={() => onSubmit(name.trim())}
          >
            Сохранить
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
